/**
 * Archive Ritual Hook
 * Handles archive/unarchive operations for rituals in a community
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { archiveRitualForCommunity, unarchiveRitualForCommunity } from "../services/ritual.service"

interface ArchiveOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export function useArchiveRitual(communityId: string) {
  const queryClient = useQueryClient()

  const archiveMutation = useMutation({
    mutationFn: (ritualId: string) => archiveRitualForCommunity(ritualId, communityId),
    onSuccess: () => {
      // Invalidate community rituals - archived ritual should disappear from "all" tab
      queryClient.invalidateQueries({
        queryKey: ["community-rituals", communityId],
      })
      // Invalidate community statistics - counts should update (includes ritual stats)
      queryClient.invalidateQueries({
        queryKey: ["community-statistics", communityId],
      })
    },
    onError: () => {
      // Error handling done via returned mutateAsync
    },
  })

  const archive = async (ritualId: string, options?: ArchiveOptions) => {
    try {
      await archiveMutation.mutateAsync(ritualId)
      options?.onSuccess?.()
    } catch (error) {
      options?.onError?.(error as Error)
    }
  }

  return {
    archive,
    isArchiving: archiveMutation.isPending,
    archiveError: archiveMutation.error,
  }
}

export function useUnarchiveRitual(communityId: string) {
  const queryClient = useQueryClient()

  const unarchiveMutation = useMutation({
    mutationFn: (ritualId: string) => unarchiveRitualForCommunity(ritualId, communityId),
    onSuccess: () => {
      // Invalidate community rituals - unarchived ritual should appear in "all" tab
      queryClient.invalidateQueries({
        queryKey: ["community-rituals", communityId],
      })
      // Invalidate community statistics - counts should update (includes ritual stats)
      queryClient.invalidateQueries({
        queryKey: ["community-statistics", communityId],
      })
    },
    onError: () => {
      // Error handling done via returned mutateAsync
    },
  })

  const unarchive = async (ritualId: string, options?: ArchiveOptions) => {
    try {
      await unarchiveMutation.mutateAsync(ritualId)
      options?.onSuccess?.()
    } catch (error) {
      options?.onError?.(error as Error)
    }
  }

  return {
    unarchive,
    isUnarchiving: unarchiveMutation.isPending,
    unarchiveError: unarchiveMutation.error,
  }
}
